import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Payment3dsService {
  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * GET /api/payment-3ds/widget-config
   * Obtiene configuracion del widget CROEM (URL, apiKey, MID, etc.)
   */
  getWidgetConfig = async (): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/widget-config`)
    );
  };

  /**
   * POST /api/payment-3ds/initiate
   * Inicia el flujo de pago: AuthenticateToken + SaleWithTdsId
   */
  initiatePayment = async (body: {
    personCode: string;
    installmentCode: string;
    amountUsd: number;
    accountToken: string;
    cvv: string;
    email?: string;
    idempotencyKey: string;
    card?: { maskedPan: string | null; holder: string | null; brand: string | null };
  }): Promise<any> => {
    return lastValueFrom(
      this.httpClient.post<any>(`${this.baseUrl}/api/payment-3ds/initiate`, body)
    );
  };

  /**
   * POST /api/payment-3ds/complete-sale
   * Completa el pago despues del challenge 3DS
   */
  completeSale = async (body: {
    clientTracking: string;
    cvv: string;
    personCode?: string;
    installmentCode?: string;
    card?: { maskedPan: string | null; holder: string | null; brand: string | null };
  }): Promise<any> => {
    return lastValueFrom(
      this.httpClient.post<any>(`${this.baseUrl}/api/payment-3ds/complete-sale`, body)
    );
  };

  /**
   * GET /api/payment-3ds/status/:clientTracking
   * Polling del estado de la transaccion
   */
  getStatus = async (clientTracking: string): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/status/${clientTracking}`)
    );
  };

  /**
   * GET /api/payment-3ds/ping
   * Health check de CROEM
   */
  ping = async (): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/ping`)
    );
  };

  /**
   * Genera un idempotencyKey unico para cada intento de pago
   */
  generateIdempotencyKey(): string {
    return 'IK-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9);
  }

  // ================================================================
  // Management endpoints
  // ================================================================

  getTransactions = async (params: any = {}): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/management/transactions`, { params })
    );
  };

  getTransactionDetail = async (id: string): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/management/transactions/${id}`)
    );
  };

  getStatistics = async (params: any = {}): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/management/statistics`, { params })
    );
  };

  getConfig = async (): Promise<any> => {
    return lastValueFrom(
      this.httpClient.get<any>(`${this.baseUrl}/api/payment-3ds/management/config`)
    );
  };

  updateConfig = async (body: { key: string; value: any; description?: string }): Promise<any> => {
    return lastValueFrom(
      this.httpClient.put<any>(`${this.baseUrl}/api/payment-3ds/management/config`, body)
    );
  };

  registerPaymentForTransaction = async (body: {
    clientTracking: string;
    personCode?: string;
    installmentCode?: string;
  }): Promise<any> => {
    return lastValueFrom(
      this.httpClient.post<any>(`${this.baseUrl}/api/payment-3ds/management/register-payment`, body)
    );
  };
}
